// Floating AI Copilot — bottom-right chat panel powered by Claude Sonnet 4.5
import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import api from "../lib/api";

const SUGGESTIONS = [
  "Comment créer mon premier workflow ?",
  "Quelles intégrations sont disponibles ?",
  "Donne-moi 3 idées d'automatisation",
];

export default function AICopilot() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Bonjour ! Je suis FlowMind Copilot. Demande-moi comment automatiser un processus, créer un workflow ou explorer le produit." },
  ]);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    const newMessages = [...messages, { role: "user", content: msg }];
    setMessages(newMessages);
    setLoading(true);
    try {
      const { data } = await api.post("/copilot/chat", {
        message: msg,
        history: newMessages.slice(-8).map((m) => ({ role: m.role, content: m.content })),
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Désolé, une erreur est survenue. Réessaie dans un instant." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      <motion.button
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={`fixed bottom-20 right-6 z-50 w-14 h-14 rounded-sm shadow-2xl flex items-center justify-center transition-colors ${
          open ? "bg-zinc-900 text-white" : "klein-bg text-white"
        }`}
        data-testid="copilot-trigger"
        aria-label="Ouvrir le copilot IA"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X size={22} strokeWidth={1.75}/>
            </motion.span>
          ) : (
            <motion.span key="s" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <Sparkles size={22} strokeWidth={1.75}/>
            </motion.span>
          )}
        </AnimatePresence>
        {!open && (
          <motion.span
            className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400"
            animate={{ scale: [1, 1.4, 1], opacity: [1, 0.5, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        )}
      </motion.button>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-6 z-50 w-[400px] max-w-[calc(100vw-3rem)] h-[560px] max-h-[calc(100vh-8rem)] bg-white border border-zinc-200 rounded-sm shadow-2xl flex flex-col overflow-hidden"
            data-testid="copilot-panel"
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between bg-black text-white">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-sm klein-bg flex items-center justify-center">
                  <Sparkles size={14}/>
                </div>
                <div>
                  <p className="font-display font-medium text-sm">FlowMind Copilot</p>
                  <p className="text-[10px] font-mono text-zinc-400 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"/>
                    Claude Sonnet 4.5
                  </p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-white" data-testid="copilot-close">
                <X size={18}/>
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAFAFA]">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 rounded-sm text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-black text-white"
                        : "bg-white border border-zinc-200"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-zinc-200 px-3.5 py-2.5 rounded-sm flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin text-zinc-500"/>
                    <span className="text-xs text-zinc-500">Le Copilot réfléchit...</span>
                  </div>
                </div>
              )}
            </div>

            {/* Suggestions (only on first message) */}
            {messages.length === 1 && !loading && (
              <div className="px-4 pb-2 space-y-1.5">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="w-full text-left text-xs text-zinc-600 hover:text-black border border-zinc-200 rounded-sm px-3 py-2 hover:border-zinc-400 transition-colors"
                    data-testid={`copilot-suggestion-${SUGGESTIONS.indexOf(s)}`}
                  >
                    → {s}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="border-t border-zinc-200 p-3 flex items-center gap-2 bg-white"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Pose une question..."
                className="flex-1 outline-none text-sm px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-sm focus:border-zinc-400"
                disabled={loading}
                data-testid="copilot-input"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="w-9 h-9 rounded-sm bg-black text-white disabled:opacity-30 flex items-center justify-center"
                data-testid="copilot-send"
              >
                <Send size={14}/>
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
